import React, { useEffect, useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import styled, { keyframes } from 'styled-components'
import { useParams } from 'react-router-dom'
import { saveAs } from 'file-saver'
import toast from 'react-hot-toast'
import { useAuth } from '../../context/AuthContext'
import { useApi } from '../../hooks/useApi'
import Calendar from '../Calendar'

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL

interface Report {
  startDate: string
  createdAt: string
  endDate: string
  updatedAt: string
  fileName: string
  mimicId: string
  _id: string
}

const DATE_FORMAT = 'MMM DD, YYYY HH:mm'

const Reports: React.FC = () => {
  const { callApi } = useApi()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { user, refreshToken, logout } = useAuth()
  const [data, setReports] = useState<Report[] | null>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [startMonth, setStartMonth] = useState<Date | undefined>(new Date())
  const [includeMode, setIncludeMode] = useState(false)
  const [includeSolana, setIncludeSolana] = useState(false)
  const [includeRainbowSummary, setIncludeRainbowSummary] = useState(false)
  const [includeBalancerSummary, setIncludeBalancerSummary] = useState(false)
  const params = useParams()

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date)
    if (date) setStartMonth(date)
  }

  const fetchReport = async () => {
    try {
      const reports = await callApi<Report[]>(`/reports/environments/${params.id}`)
      setReports(reports)
    } catch (error) {
      toast.error('Failed to fetch reports.')
    }
  }

  const refetchReport = async () => {
    setIsRefreshing(true)
    try {
      const reports = await callApi<Report[]>(`/reports/environments/${params.id}`)
      setReports(reports)
      toast.success('Reports updated!')
    } catch (error) {
      toast.error('Failed to fetch reports.')
    } finally {
      setIsRefreshing(false)
    }
  }

  useEffect(() => { fetchReport() }, [])

  const handleDownload = async (id: string, fileName: string) => {
    const url = `${BASE_URL}/reports/environments/${id}/download`
    try {
      const toastId = toast.loading('Downloading report...')
      const response = await axios.get(url, {
        headers: { 'x-auth-token': `${user?.token}` },
        responseType: 'blob',
      })
      saveAs(new Blob([response.data]), fileName)
      toast.success('Downloaded successfully', { id: toastId })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 403) {
          toast("You don't have permissions to download this report")
        } else if (error?.response?.status === 401) {
          const tokenRefreshed = await refreshToken()
          if (tokenRefreshed) return await handleDownload(id, fileName)
          else { toast.error('Session expired. Please log in again.'); logout(); return null }
        } else {
          toast("Couldn't download the report")
        }
      }
    }
  }

  const generateReport = async () => {
    if (!startDate || !endDate) {
      toast('Complete both dates before generating')
      return
    }
    const adjustedStart = moment.utc(startDate).set({ hour: startDate.getHours(), minute: startDate.getMinutes(), second: 0, millisecond: 0 }).toISOString()
    const adjustedEnd = moment.utc(endDate).set({ hour: endDate.getHours(), minute: endDate.getMinutes(), second: 0, millisecond: 0 }).toISOString()
    try {
      await callApi('/jobs/report', {
        startDate: adjustedStart,
        endDate: adjustedEnd,
        mimicId: params.id,
        mode: includeMode ? 'general_report_assets_balance' : 'general_report',
        ...(includeSolana ? { includeSolanaProcessedData: true } : {}),
        ...(includeRainbowSummary ? { includeRainbowSummary: true } : {}),
        includeBalancerSummary,
      }, 'POST')
      fetchReport()
    } catch (error) {
      console.error('Error generating report:', error)
    }
  }

  return (
    <Wrap>


      <Toolbar>
        <Calendar label="From date" selectedDate={startDate} withHour={true} onChange={handleStartDateChange} />
        <Calendar label="To date" selectedDate={endDate} startMonth={startMonth} withHour={true} onChange={(d) => setEndDate(d)} />

        <CheckGroup>
          <CheckLabel>
            <input type="checkbox" checked={includeMode} onChange={(e) => setIncludeMode(e.target.checked)} />
            Assets balances
          </CheckLabel>
          <CheckLabel>
            <input type="checkbox" checked={includeSolana} onChange={(e) => setIncludeSolana(e.target.checked)} />
            Solana
          </CheckLabel>
          <CheckLabel>
            <input type="checkbox" checked={includeRainbowSummary} onChange={(e) => setIncludeRainbowSummary(e.target.checked)} />
            Rainbow summary
          </CheckLabel>
          <CheckLabel>
            <input type="checkbox" checked={includeBalancerSummary} onChange={(e) => setIncludeBalancerSummary(e.target.checked)} />
            Balancer summary
          </CheckLabel>
        </CheckGroup>

        <ToolbarRight>
          <PrimaryBtn onClick={generateReport}>Generate report</PrimaryBtn>
          <IconBtn onClick={refetchReport} disabled={isRefreshing} title="Refresh">
            {isRefreshing ? <Spinner /> : <RefreshIcon />}
          </IconBtn>
        </ToolbarRight>
      </Toolbar>

      {data === null ? (
        <LoaderBar />
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>File name</Th>
                <Th>From (UTC)</Th>
                <Th>To (UTC)</Th>
                <Th>Created at (UTC)</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {data
                .slice()
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((item, index) => (
                  <tr key={index}>
                    <Td><FileName>{item.fileName}</FileName></Td>
                    <Td>{moment.utc(item.startDate).format(DATE_FORMAT)}</Td>
                    <Td>{moment.utc(item.endDate).format(DATE_FORMAT)}</Td>
                    <Td>{moment.utc(item.createdAt).format(DATE_FORMAT)}</Td>
                    <Td><ActionBtn onClick={() => handleDownload(item._id, item.fileName)}>Download ↓</ActionBtn></Td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Wrap>
  )
}

export default Reports

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`

const CheckGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  padding: 0 4px;
`

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #777;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.15s;

  input[type='checkbox'] {
    accent-color: #6f5ce6;
    width: 13px;
    height: 13px;
    cursor: pointer;
  }

  &:hover { color: #a0a0a0; }
`

const PrimaryBtn = styled.button`
  height: 34px;
  padding: 0 16px;
  background: rgba(111, 92, 230, 0.15);
  border: 1px solid rgba(111, 92, 230, 0.35);
  border-radius: 6px;
  color: #a89aff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: rgba(111, 92, 230, 0.25);
    border-color: rgba(111, 92, 230, 0.5);
  }
`

const ActionBtn = styled.button`
  height: 28px;
  padding: 0 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 5px;
  color: #a0a0a0;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: rgba(111, 92, 230, 0.4);
    color: #a89aff;
  }
`

const IconBtn = styled.button`
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #777;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover:not(:disabled) { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }
  &:disabled { opacity: 0.4; cursor: default; }
`

const spin = keyframes`to { transform: rotate(360deg); }`

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(111, 92, 230, 0.2);
  border-top-color: #6f5ce6;
  animation: ${spin} 0.7s linear infinite;
`

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255,255,255,0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`

const Td = styled.td`
  padding: 10px 12px;
  color: #a0a0a0;
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(255,255,255,0.04);

  &:last-child { border-bottom: none; }
  tr:last-child & { border-bottom: none; }
`

const FileName = styled.span`
  color: #ededed;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
`

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="m3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
)
